import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, MapPin, Layers, Loader2, AlertTriangle, ChevronRight, Target, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';
import CarrierLogo from '../components/CarrierLogo';

const flag = (iso2) => (!iso2 || iso2.length !== 2) ? ''
  : String.fromCodePoint(...iso2.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65));

// A range is stored as integers, so a Spanish 03510 comes back as 3510.
// Everything shown to a human has to be padded back to the zone's real width.
const pad = (n, w) => String(n).padStart(w || 0, '0');
const fmtRange = (a, b, w) => a === b ? pad(a, w) : `${pad(a, w)} – ${pad(b, w)}`;

/** OVERRIDE vs REPLACEMENT, in words rather than a boolean nobody remembers. */
function KindBadge({ kind }) {
  const override = kind === 'override';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${
        override ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-900'}`}
      title={override
        ? 'Override: adds an uplift for the carriers listed here. The country’s other carriers still serve this postcode at their country rates.'
        : 'Replacement: this zone REPLACES the country rates. A carrier with no row here does not serve these postcodes at all, and this zone outranks every override zone whatever their precision.'}
    >
      {override ? 'override' : 'replacement'}
    </span>
  );
}

export default function ZoneExplorer() {
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [kind, setKind] = useState('');

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rangeQuery, setRangeQuery] = useState('');
  const [rangeOffset, setRangeOffset] = useState(0);

  const [lookupCountry, setLookupCountry] = useState('');
  const [lookupPc, setLookupPc] = useState('');
  const [lookup, setLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    axiosInstance.get('api/zones/overview/')
      .then(r => { setZones(r.data?.zones || []); setCarriers(r.data?.carriers || []); })
      .catch(e => setError(
        e?.response?.status
          ? `Could not load zones (HTTP ${e.response.status} from /zones/overview/). ${e.response.status === 403 ? 'The session may have expired, try reloading.' : ''}`
          : `Could not reach the zones API: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  const loadDetail = useCallback((id, search, offset) => {
    setDetailLoading(true);
    axiosInstance.get(`api/zones/${id}/`, { params: { q: search || undefined, offset, limit: 300 } })
      .then(r => setDetail(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setDetailLoading(false));
  }, []);

  const pick = (z) => {
    setSelected(z); setRangeQuery(''); setRangeOffset(0); setDetail(null);
    loadDetail(z.id, '', 0);
  };

  const runLookup = () => {
    if (!lookupCountry || !lookupPc) return;
    setLookupLoading(true); setLookup(null);
    axiosInstance.get('api/zones/lookup/', { params: { country: lookupCountry, postal_code: lookupPc } })
      .then(r => setLookup(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setLookupLoading(false));
  };

  const countries = useMemo(
    () => [...new Set(zones.map(z => `${z.country_iso2}|${z.country_name}`))].sort(), [zones]);

  const shown = useMemo(() => zones.filter(z => {
    if (country && z.country_iso2 !== country) return false;
    if (kind && z.kind !== kind) return false;
    if (carrierId && !z.carriers.some(c => String(c.id) === carrierId)) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!z.name.toLowerCase().includes(t) && !z.country_name.toLowerCase().includes(t)) return false;
    }
    return true;
  }), [zones, country, kind, carrierId, q]);

  const totals = useMemo(() => ({
    zones: shown.length,
    ranges: shown.reduce((s, z) => s + z.range_count, 0),
    span: shown.reduce((s, z) => s + z.postcode_span, 0),
  }), [shown]);

  if (loading) return <ShippyLoader />;

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Layers className="w-6 h-6 text-sky-600" /> Zones
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Which postcodes a zone claims, which carriers it prices, and why a postcode resolves the way it does.
          </p>
        </div>
        <div className="text-sm text-gray-600 flex gap-5">
          <span><b>{totals.zones}</b> zones</span>
          <span><b>{totals.ranges.toLocaleString()}</b> ranges</span>
          <span><b>{totals.span.toLocaleString()}</b> postcodes covered</span>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ---- postcode lookup: the question people actually arrive with ---- */}
      <div className="rounded-lg border bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-sky-600" />
          <h2 className="font-medium">Where does a postcode land?</h2>
          <span className="text-xs text-gray-500">resolved by the live pricing engine, not a second implementation</span>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select className="h-9 rounded border px-2 text-sm" value={lookupCountry}
                  onChange={e => setLookupCountry(e.target.value)}>
            <option value="">Country…</option>
            {countries.map(c => { const [iso, name] = c.split('|');
              return <option key={iso} value={name}>{flag(iso)} {name}</option>; })}
          </select>
          <Input className="h-9 w-44" placeholder="Postal code" value={lookupPc}
                 onChange={e => setLookupPc(e.target.value)}
                 onKeyDown={e => e.key === 'Enter' && runLookup()} />
          <Button size="sm" onClick={runLookup} disabled={!lookupCountry || !lookupPc || lookupLoading}>
            {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Resolve'}
          </Button>
        </div>

        {lookup && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-gray-500">Governing zone:</span>
              {lookup.governing_zone ? (
                <>
                  <b>{lookup.governing_zone.name}</b>
                  <KindBadge kind={lookup.governing_zone.kind} />
                </>
              ) : <span className="text-gray-600">none — prices at the country card</span>}
            </div>
            {lookup.matching_zones?.length > 1 && (
              <div className="text-xs text-gray-600">
                Also matched: {lookup.matching_zones.filter(m => !m.governing).map(m => m.name).join(', ')}
                <span className="text-gray-400"> — these do not govern, but a carrier they price still takes its row from them.</span>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b">
                    <th className="py-1.5 pr-3">Carrier</th>
                    <th className="py-1.5 pr-3 text-right">Country</th>
                    <th className="py-1.5 pr-3 text-right">Here</th>
                    <th className="py-1.5 pr-3 text-right">Uplift</th>
                    <th className="py-1.5">Row comes from</th>
                  </tr>
                </thead>
                <tbody>
                  {lookup.carriers.map(c => (
                    <tr key={c.carrier_id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3">
                        <span className="inline-flex items-center gap-2">
                          <CarrierLogo name={c.carrier} showName={false} height={16} />{c.carrier}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">
                        {c.country_cost != null ? `€${c.country_cost.toFixed(2)}` : '—'}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums font-medium">€{c.cost.toFixed(2)}</td>
                      <td className={`py-1.5 pr-3 text-right tabular-nums ${c.uplift > 0 ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>
                        {c.uplift ? `+€${c.uplift.toFixed(2)}` : '—'}</td>
                      <td className="py-1.5 text-xs text-gray-600">{c.row_from || <span className="text-gray-400">country card</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400">
              Reference parcel {lookup.reference_parcel.width}×{lookup.reference_parcel.height}×{lookup.reference_parcel.depth} cm, {lookup.reference_parcel.weight_kg} kg.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-5 items-start">
        {/* ---- zone list ---- */}
        <div className="rounded-lg border bg-white">
          <div className="p-3 space-y-2 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <Input className="h-9 pl-8" placeholder="Filter zones…" value={q}
                     onChange={e => setQ(e.target.value)} />
            </div>
            <div className="flex gap-2 flex-wrap">
              <select className="h-8 rounded border px-2 text-xs" value={country}
                      onChange={e => setCountry(e.target.value)}>
                <option value="">All countries</option>
                {countries.map(c => { const [iso, name] = c.split('|');
                  return <option key={iso} value={iso}>{flag(iso)} {name}</option>; })}
              </select>
              <select className="h-8 rounded border px-2 text-xs" value={carrierId}
                      onChange={e => setCarrierId(e.target.value)}>
                <option value="">All carriers</option>
                {carriers.map(c => <option key={c.id} value={String(c.id)}>
                  {c.name}{c.is_active ? '' : ' (inactive)'}</option>)}
              </select>
              <select className="h-8 rounded border px-2 text-xs" value={kind}
                      onChange={e => setKind(e.target.value)}>
                <option value="">Any kind</option>
                <option value="override">Override</option>
                <option value="replacement">Replacement</option>
              </select>
            </div>
          </div>
          <div className="max-h-[560px] overflow-y-auto divide-y">
            {shown.map(z => (
              <button key={z.id} onClick={() => pick(z)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${selected?.id === z.id ? 'bg-sky-50' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm truncate">
                    {flag(z.country_iso2)} {z.name}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <KindBadge kind={z.kind} />
                  {!z.is_active && <span className="text-[11px] text-gray-400">inactive</span>}
                  <span className="text-[11px] text-gray-500">
                    {z.range_count ? `${z.range_count.toLocaleString()} ranges` : ''}
                    {z.range_count && z.prefix_count ? ' · ' : ''}
                    {z.prefix_count ? `${z.prefix_count} prefixes` : ''}
                  </span>
                  <span className="flex -space-x-1">
                    {z.carriers.slice(0, 6).map(c => (
                      <CarrierLogo key={c.id} name={c.name} showName={false} height={14} />
                    ))}
                  </span>
                </div>
              </button>
            ))}
            {!shown.length && <div className="p-6 text-sm text-gray-500 text-center">No zone matches.</div>}
          </div>
        </div>

        {/* ---- zone detail ---- */}
        <div className="rounded-lg border bg-white min-h-[300px]">
          {!selected && (
            <div className="p-10 text-center text-gray-500 text-sm">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              Pick a zone to see the postcodes it claims and the bands it prices.
            </div>
          )}
          {selected && detailLoading && !detail && (
            <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
          )}
          {detail && (
            <div className="p-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">{flag(detail.zone.country_iso2)} {detail.zone.name}</h2>
                  <KindBadge kind={detail.zone.kind} />
                  {!detail.zone.is_active && <span className="text-xs text-gray-400">inactive</span>}
                </div>
                <div className="flex gap-5 text-xs text-gray-600 mt-1.5 flex-wrap">
                  <span>{detail.zone.range_count.toLocaleString()} ranges</span>
                  <span>{detail.zone.postcode_span.toLocaleString()} postcodes</span>
                  {detail.zone.franco_threshold != null &&
                    <span>franco €{detail.zone.franco_threshold}</span>}
                  {detail.zone.vat_rate != null && <span>VAT {detail.zone.vat_rate}%</span>}
                </div>
              </div>

              {detail.zone.notes && (
                <div className="flex gap-2 rounded bg-gray-50 border px-3 py-2 text-xs text-gray-700">
                  <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                  <span>{detail.zone.notes}</span>
                </div>
              )}

              {detail.zone.prefixes.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase text-gray-500 mb-1.5">Prefixes</h3>
                  <div className="flex flex-wrap gap-1">
                    {detail.zone.prefixes.map(p => (
                      <span key={p} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono">{p}</span>
                    ))}
                  </div>
                </div>
              )}

              {detail.zone.range_count > 0 && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <h3 className="text-xs uppercase text-gray-500">
                      Postcode ranges
                      <span className="ml-2 normal-case text-gray-400">
                        {detail.ranges.total.toLocaleString()} matching
                      </span>
                    </h3>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-gray-400" />
                      <Input className="h-8 w-44 pl-7 text-xs" placeholder="Find a postcode…"
                             value={rangeQuery}
                             onChange={e => { setRangeQuery(e.target.value); setRangeOffset(0);
                                              loadDetail(selected.id, e.target.value, 0); }} />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto rounded border bg-gray-50 p-2">
                    <div className="flex flex-wrap gap-1">
                      {detail.ranges.items.map(([a, b], i) => (
                        <span key={i} className="rounded bg-white border px-1.5 py-0.5 text-[11px] font-mono tabular-nums">
                          {fmtRange(a, b, detail.zone.range_width)}
                        </span>
                      ))}
                    </div>
                    {!detail.ranges.items.length && (
                      <div className="text-xs text-gray-500 py-4 text-center">
                        No range here matches “{rangeQuery}”.
                      </div>
                    )}
                  </div>
                  {detail.ranges.total > detail.ranges.items.length && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button size="sm" variant="outline" disabled={rangeOffset === 0}
                              onClick={() => { const o = Math.max(0, rangeOffset - 300);
                                               setRangeOffset(o); loadDetail(selected.id, rangeQuery, o); }}>
                        Previous
                      </Button>
                      <span className="text-xs text-gray-500">
                        {rangeOffset + 1}–{Math.min(rangeOffset + 300, detail.ranges.total)} of {detail.ranges.total.toLocaleString()}
                      </span>
                      <Button size="sm" variant="outline"
                              disabled={rangeOffset + 300 >= detail.ranges.total}
                              onClick={() => { const o = rangeOffset + 300;
                                               setRangeOffset(o); loadDetail(selected.id, rangeQuery, o); }}>
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div>
                <h3 className="text-xs uppercase text-gray-500 mb-1.5">Carriers priced here</h3>
                {!detail.bands.length && (
                  <p className="text-xs text-gray-500">
                    No rate rows. {detail.zone.kind === 'replacement'
                      ? 'As a replacement zone with no rows, nothing can price these postcodes.'
                      : 'Every carrier falls through to the country card here.'}
                  </p>
                )}
                <div className="space-y-2">
                  {detail.bands.map(b => (
                    <details key={`${b.carrier_id}|${b.table}`} className="rounded border">
                      <summary className="cursor-pointer px-3 py-2 text-sm flex items-center gap-2">
                        <CarrierLogo name={b.carrier} showName={false} height={16} />
                        <span className="font-medium">{b.carrier}</span>
                        <span className="text-xs text-gray-500">{b.table} · {b.rows.length} bands</span>
                      </summary>
                      <div className="px-3 pb-3 flex flex-wrap gap-1">
                        {b.rows.map(r => (
                          <span key={r.weight_kg} className="rounded bg-gray-50 border px-1.5 py-0.5 text-[11px] tabular-nums">
                            {r.weight_kg}kg <b>€{Number(r.cost).toFixed(2)}</b>
                          </span>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
